import type { TFunction } from "i18next";
import type {
  AgentRegistryRequestModel,
  Configuration,
  ExternalIntegrationModel,
} from "@/api";
import type { AgentPlatform } from "@/api/temp/AgentEstateService";
import { ApiErrorDetail } from "@/helpers/apiErrorDetail";
import { toRequiredStringValue } from "@/helpers/formValues";
import {
  REGISTRY_ERROR_CODES,
  RegistryCheckGroup,
  RegistryProject,
} from "./types";

export const REGISTRY_I18N = "agentEstate.wizard.registry";

/**
 * The providers whose onboarding runs against the real agent-registry API.
 * Every other provider keeps its preview journey. The one place the wizard
 * decides which flow it is in.
 */
/** The platform an `AZURE_AGENT_REGISTRY` connection is onboarded through. */
export const REGISTRY_PLATFORM: AgentPlatform = "MICROSOFT_FOUNDRY";

const LIVE_REGISTRY_PLATFORMS: readonly AgentPlatform[] = [REGISTRY_PLATFORM];

export const isLiveRegistryPlatform = (platform: unknown): boolean =>
  typeof platform === "string" &&
  LIVE_REGISTRY_PLATFORMS.includes(platform as AgentPlatform);

export const AZURE_AGENT_REGISTRY = "AZURE_AGENT_REGISTRY";

/** The only identity binding the Azure registry supports. */
export const REGISTRY_IDENTITY = {
  provider: "ENTRA_ID",
  credentialSource: "REGISTRY_CONNECTION",
} as const;

export const REGISTRY_NAME_MAX_LENGTH = 255;

/**
 * The credential fields a registry test proves, and so what invalidates one.
 * The connection name rides along in every request but proves nothing about
 * access: renaming the connection keeps its test results.
 */
export const CREDENTIAL_VALUE_FIELDS = [
  "account",
  "clientId",
  "authorizationToken",
] as const;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type ValueGetter = (name: string) => unknown;

/** The secret exactly as typed - not trimmed, since whitespace may be part of it. */
const rawSecret = (read: ValueGetter): string => {
  const value = read("authorizationToken");
  return typeof value === "string" ? value : "";
};

/**
 * The wizard's fields as the API's `configuration` - the one mapper validate,
 * preview and onboard all send, so the three can never describe different
 * registries.
 *
 * The Entra tenant ID is the customer's Microsoft tenant, never the TLX
 * tenant: that one travels in the `tenantId` header, set by the API client.
 */
export const toRegistryConfiguration = (read: ValueGetter): Configuration => {
  const secret = rawSecret(read);
  // Set only when editing: its presence is what makes the import an update.
  const id = toRequiredStringValue(read("registryId"));
  return {
    ...(id ? { id } : {}),
    integrationType: AZURE_AGENT_REGISTRY,
    name: toRequiredStringValue(read("connectionName")),
    connection: {
      tenantId: toRequiredStringValue(read("account")),
      clientId: toRequiredStringValue(read("clientId")),
      clientSecret: secret.trim() ? secret : "",
    },
    identity: { ...REGISTRY_IDENTITY },
  };
};

/**
 * A saved registry as the wizard's draft, from the list response. The secret
 * stays masked; sent back unchanged, the API keeps the stored one.
 */
export const toRegistryFormValues = (registry: ExternalIntegrationModel) => {
  const details = registry.integrationDetails;
  return {
    registryId: registry.id ?? "",
    platform: REGISTRY_PLATFORM,
    connectionName: details?.name ?? "",
    account: details?.tenantId ?? "",
    clientId: details?.clientId ?? "",
    authorizationToken: details?.clientSecret ?? "",
    detectedIdentityProvider: "MICROSOFT_ENTRA_ID",
  };
};

/**
 * How the selection is sent: `ALL_LISTED` (the server's default) must name
 * every listed project; `SELECTED` names any non-empty subset of them.
 */
export const PROJECT_SELECTION_MODES = {
  ALL_LISTED: "ALL_LISTED",
  SELECTED: "SELECTED",
} as const;

/**
 * The onboarding body: the configuration, the administrator's MCP-services
 * confirmation and exactly the selected projects, out of `listedCount` the
 * preview listed.
 *
 * No `gatewayId`: the server resolves the tenant's own gateway (its URL
 * identifies it) and ignores one sent by the client, answering
 * `GATEWAY_REQUIRED` when the tenant has none.
 */
export const toOnboardingRequest = (
  configuration: Configuration,
  mcpServicesConfirmed: boolean,
  selected: RegistryProject[],
  listedCount: number,
): AgentRegistryRequestModel => {
  const projectIds = selected.map(project => project.id);
  return {
    configuration,
    prerequisites: { requiredMcpServicesConfirmed: mcpServicesConfirmed },
    projectSelection: {
      mode:
        projectIds.length === listedCount
          ? PROJECT_SELECTION_MODES.ALL_LISTED
          : PROJECT_SELECTION_MODES.SELECTED,
      projectIds,
    },
  };
};

/**
 * A step passes on its own result only - an HTTP 200 is not a pass, and nor is
 * a "PASSED" group whose own checks include one that did not pass.
 */
export const hasPassed = (group?: RegistryCheckGroup): boolean =>
  group?.status === "PASSED" &&
  (group.checks ?? []).every(check => check.status === "PASSED");

/* ------------------------------------------------------------------------ */
/* Field validators: they return i18n keys; `withMessages` translates them. */
/* ------------------------------------------------------------------------ */

const FIELD_KEY = `${REGISTRY_I18N}.validation`;

export const registryNameError = (value: unknown): string | undefined => {
  const name = toRequiredStringValue(value);
  if (!name) return `${FIELD_KEY}.nameRequired`;
  if (name.length > REGISTRY_NAME_MAX_LENGTH) return `${FIELD_KEY}.nameTooLong`;
  return undefined;
};

const uuidError =
  (requiredKey: string) =>
  (value: unknown): string | undefined => {
    const id = toRequiredStringValue(value);
    if (!id) return `${FIELD_KEY}.${requiredKey}`;
    return UUID_PATTERN.test(id) ? undefined : `${FIELD_KEY}.uuid`;
  };

export const tenantIdError = uuidError("tenantIdRequired");
export const clientIdError = uuidError("clientIdRequired");

/** A new registry always needs the real secret. */
export const clientSecretError = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim()
    ? undefined
    : `${FIELD_KEY}.secretRequired`;

export const withMessage =
  (t: TFunction, check: (value: unknown) => string | undefined) =>
  (value: unknown): string | undefined => {
    const key = check(value);
    return key ? t(key) : undefined;
  };

/* ------------------------------------------------------------------------ */
/* Error and check messages                                                  */
/* ------------------------------------------------------------------------ */

const ERROR_KEY = `${REGISTRY_I18N}.errors`;

const CODE_MESSAGE_KEYS: Record<string, string> = {
  [REGISTRY_ERROR_CODES.SECRET_REQUIRED]: `${ERROR_KEY}.secretRequired`,
  [REGISTRY_ERROR_CODES.SECRET_REQUIRED_FOR_IDENTITY_CHANGE]: `${ERROR_KEY}.secretRequiredForIdentityChange`,
  [REGISTRY_ERROR_CODES.PREVIEW_INCOMPLETE]: `${ERROR_KEY}.previewIncomplete`,
  [REGISTRY_ERROR_CODES.PROJECT_SELECTION_CHANGED]: `${ERROR_KEY}.projectSelectionChanged`,
  [REGISTRY_ERROR_CODES.EMPTY_PROJECT_SELECTION]: `${ERROR_KEY}.emptyProjectSelection`,
  [REGISTRY_ERROR_CODES.MCP_CONFIRMATION_REQUIRED]: `${ERROR_KEY}.mcpConfirmationRequired`,
  [REGISTRY_ERROR_CODES.NOT_FOUND]: `${ERROR_KEY}.notFound`,
  [REGISTRY_ERROR_CODES.REGISTRY_CHANGED]: `${ERROR_KEY}.registryChanged`,
  [REGISTRY_ERROR_CODES.AGENT_LIST_INCOMPLETE]: `${ERROR_KEY}.agentListIncomplete`,
  [REGISTRY_ERROR_CODES.GATEWAY_REQUIRED]: `${ERROR_KEY}.gatewayRequired`,
  [REGISTRY_ERROR_CODES.INVALID_CREDENTIALS]: `${ERROR_KEY}.invalidCredentials`,
};

const statusMessageKey = (status?: number): string => {
  if (status === 400) return `${ERROR_KEY}.invalid`;
  if (status === 403) return `${ERROR_KEY}.forbidden`;
  if (status === 404) return `${ERROR_KEY}.notFound`;
  if (status === 409) return `${ERROR_KEY}.conflict`;
  if (status === 502 || status === 503) return `${ERROR_KEY}.upstream`;
  return `${ERROR_KEY}.generic`;
};

/**
 * One actionable sentence for a failed registry call. A known code wins; then
 * the server's own message, which names the field, the duplicate or the
 * missing Microsoft permission; then a sentence for the HTTP status. The
 * request body - and so the secret - is never part of it.
 */
export const registryErrorMessage = (
  t: TFunction,
  detail: ApiErrorDetail,
): string => {
  const codeKey = detail.code ? CODE_MESSAGE_KEYS[detail.code] : undefined;
  if (codeKey) return t(codeKey);
  if (detail.message) return detail.message;
  return t(statusMessageKey(detail.status));
};

/** Checks the UI has its own advice for; any other code falls back to the server message. */
const KNOWN_CHECKS = [
  "PROVIDER_AUTHENTICATION",
  "PROJECT_DISCOVERY_ACCESS",
  "AGENT_IDENTITY_READ",
  "AGENT_ATTRIBUTE_READ",
];

export const checkLabelKey = (code: string): string =>
  KNOWN_CHECKS.includes(code)
    ? `${REGISTRY_I18N}.checks.${code}.label`
    : `${REGISTRY_I18N}.checks.unknown`;

export const checkAdviceKey = (code: string): string | undefined =>
  KNOWN_CHECKS.includes(code)
    ? `${REGISTRY_I18N}.checks.${code}.advice`
    : undefined;
